import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { CartService } from '../../services/cart';
import { LanguageService } from '../../services/language.service';
import { BnNumberPipe } from '../../pipes/bn-number.pipe';

@Component({
  selector: 'app-product-details',
  standalone: true,
  imports: [CommonModule, RouterLink, BnNumberPipe],
  templateUrl: './product-details.html',
  styleUrls: ['./product-details.css']
})
export class ProductDetails implements OnInit, OnDestroy {

  product: any;

  notFound = false;

  selectedImage = '';

  images: string[] = [];

  quantity = 0;

  // একই ক্যাটাগরির অন্য প্রোডাক্ট (ডান পাশের "Related Products")
  related: any[] = [];

  // Summary অংশ: Show More / Show Less
  showFullDesc = false;

  readonly DESC_LIMIT = 420;

  apiUrl =
    'https://superbangladesh-api-1.onrender.com';

  private sub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private cart: CartService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    public languageService: LanguageService
  ) {}

  t(key: string): string {
    return this.languageService.translate(key);
  }

  categoryName(name: string, nameBn?: string | null): string {
    return this.languageService.translateCategory(name, nameBn);
  }

  ngOnInit(): void {

    // রিলেটেড প্রোডাক্টে ক্লিক করলে একই কম্পোনেন্ট থাকে, শুধু id বদলায় —
    // তাই snapshot নয়, paramMap সাবস্ক্রাইব করা হয়েছে
    this.sub = this.route.paramMap.subscribe(params => {

      const id = params.get('id');

      if (id) {
        this.loadProduct(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private loadProduct(id: string): void {

    this.product = null;

    this.notFound = false;

    this.related = [];

    this.showFullDesc = false;

    this.http.get(
      `${this.apiUrl}/api/products/${id}`
    ).subscribe({

      next: (res: any) => {

        if (!res) {
          this.notFound = true;
          return;
        }

        this.product = res;

        this.images = [
          res.imageUrl,
          res.imageUrl2,
          res.imageUrl3,
          res.imageUrl4
        ].filter((u: any) => !!u);

        this.selectedImage = this.images[0] || '';

        this.quantity = this.cart.getQty(res.id);

        this.loadRelated(res);

        window.scrollTo({ top: 0 });

        this.cdr.markForCheck();
      },

      error: () => {
        this.notFound = true;

        this.cdr.markForCheck();
      }
    });
  }

  private loadRelated(p: any): void {

    const catId = p?.category?.id;

    if (!catId) {
      return;
    }

    this.http
      .get<any[]>(`${this.apiUrl}/api/products/category/${catId}`)
      .subscribe({

        next: (list: any[]) => {

          this.related =
            (Array.isArray(list) ? list : [])
              .filter(x => x.id !== p.id && x.stock > 0)
              .slice(0, 8);

          this.cdr.markForCheck();
        },

        error: () => {
          this.related = [];

          this.cdr.markForCheck();
        }
      });
  }

  // IMAGE SUPPORT
  getImage(url: string): string {

    if (!url) {

      return 'assets/no-image.png';
    }

    // Cloudinary URL
    if (url.startsWith('http')) {

      return url;
    }

    // Old Upload Folder
    return `${this.apiUrl}/uploads/${url}`;
  }

  onImgError(event: Event): void {

    (event.target as HTMLImageElement).src = 'assets/no-image.png';
  }

  getOriginName(origin: string): string {

    if (!origin) {
      return '';
    }

    return origin
      .replace(/^product\s+of\s+/i, '')
      .trim();
  }

  // =========================
  // PRICE
  // =========================

  getFinalPrice(p: any): number {

    const price = Number(p?.price) || 0;
    const discount = Number(p?.discount) || 0;

    if (!discount) {
      return price;
    }

    return Math.round(price - (price * discount / 100));
  }

  get savedAmount(): number {

    return Math.max(
      0,
      (Number(this.product?.price) || 0) - this.getFinalPrice(this.product)
    );
  }

  // =========================
  // CATEGORY (breadcrumb + spec)
  // =========================

  get categoryLabel(): string {

    const c = this.product?.category;

    return c?.name
      ? this.categoryName(c.name, c.nameBn)
      : '';
  }

  // =========================
  // SPECIFICATION TABLE (শুধু যেগুলোর মান আছে)
  // =========================

  get specRows(): { label: string; value: string }[] {

    const p = this.product;

    if (!p) {
      return [];
    }

    const rows: { label: string; value: string }[] = [];

    const add = (label: string, value: any) => {

      const v = String(value ?? '').trim();

      if (v) {
        rows.push({ label, value: v });
      }
    };

    add(this.t('specName'), this.languageService.productName(p));

    add(this.t('categoryLabel'), this.categoryLabel);

    add(this.t('brandLabel'), p.brand);

    add(
      this.t('countryOfOrigin'),
      this.languageService.translateOrigin(this.getOriginName(p.origin))
    );

    if (p.quantity && p.unit) {
      add(
        this.t('sizeLabel'),
        `${p.quantity} ${this.languageService.translateUnit(p.unit)}`
      );
    }

    add(this.t('weightLabel'), p.weight);

    add('SKU', p.sku);

    add('Barcode', p.barcode);

    return rows;
  }

  // =========================
  // SUMMARY (Show More)
  // =========================

  get descriptionText(): string {

    return this.languageService.productDescription(this.product).trim();
  }

  get descriptionIsLong(): boolean {

    return this.descriptionText.length > this.DESC_LIMIT;
  }

  // =========================
  // CART
  // =========================

  increaseQty(): void {

    this.cart.increaseByProduct({
      ...this.product,
      price: this.getFinalPrice(this.product)
    });

    this.quantity =
      this.cart.getQty(this.product.id);

  }

  decreaseQty(): void {

    this.cart.decreaseByProduct(this.product);

    this.quantity =
      this.cart.getQty(this.product.id);

  }

  addToCart(): void {

    this.cart.add({
      ...this.product,
      price: this.getFinalPrice(this.product)
    });

    this.quantity =
      this.cart.getQty(this.product.id);

  }

  buyNow(): void {

    // আগে থেকেই কার্টে থাকলে আবার যোগ না করে সরাসরি কার্টে যাবে
    if (this.quantity <= 0) {
      this.addToCart();
    }

    this.router.navigate(['/cart']);

  }

  goToCart(): void {

    this.router.navigate(['/cart']);
  }

  openRelated(id: number): void {

    this.router.navigate(['/product', id]);
  }

}
